import React, { useRef, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogContent,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { Close, UploadFile, PlaylistAddCheck, Download, InsertDriveFile } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import { WarehouseAPI } from '../../services/api';
import { PackagingPerSkuDiff } from '../../types';
import { tokens } from '../../theme/tokens';

interface BulkPackagingImportDialogProps {
    open: boolean;
    onClose: () => void;
    onApplied: () => void;
}

const chipMono = { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.65rem', fontWeight: 700 } as const;

// The exact template the upload expects: two columns, header row, the Packaging SKUs cell is a
// quoted comma-separated list. Offered via "Download template" so users fill the right shape.
const TEMPLATE_CSV =
    'SKU,Packaging SKUs\n' +
    'SG0082-BK,"SINGLE-WATCH-BOX-ORANGE,WT-WARRANTYCARD"\n' +
    'EXAMPLE-SKU-2,"PLASTICPOUCH-4.5X6"\n';

// Caption shown under a main-SKU-rejected row (mirrors the backend MAIN_SKU_REASON intent).
const MAIN_SKU_REASON = 'Main SKU not found in the Plytix catalogue';

// Quote a CSV cell only when it contains a comma, a double quote, or a newline. Inner double
// quotes are doubled (RFC-4180). Plain cells pass through unchanged — mirrors the backend
// csvCell so the export stays diff-friendly.
const csvCell = (value: string): string => {
    const s = String(value);
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
        return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
};

// A result row is "invalid" (and belongs in the downloadable CSV) iff its main SKU was
// rejected OR it had at least one rejected packaging SKU. Clean 'imported'/'no-op' rows are
// excluded.
const isInvalidRow = (r: PackagingPerSkuDiff): boolean =>
    r.mainSkuValid === false || r.rejected.length > 0;

// Build the invalid-rows CSV: one row per rejected SKU, with a typed Error Type and a Reason
// that distinguishes a bad main SKU from bad packaging SKUs. Error Type is mutually exclusive
// by construction — a main-invalid row never has its packaging evaluated.
const buildInvalidRowsCsv = (results: PackagingPerSkuDiff[]): string => {
    const header = 'SKU,Packaging SKUs,Error Type,Reason';
    const lines = [header];
    for (const r of results) {
        if (!isInvalidRow(r)) continue;
        const errorType = r.mainSkuValid === false ? 'MAIN_SKU' : 'PACKAGING_SKU';
        const reason =
            r.mainSkuValid === false
                ? 'main SKU invalid'
                : 'packaging SKU invalid: ' + r.rejected.map(x => x.sku).join(', ');
        lines.push(
            [
                csvCell(r.sku),
                csvCell((r.submittedPackaging || []).join(',')),
                csvCell(errorType),
                csvCell(reason),
            ].join(',')
        );
    }
    return lines.join('\r\n');
};

// Two-phase bulk CSV importer for per-SKU packaging mappings. The user downloads the template,
// fills it (2 columns: SKU, Packaging SKUs — the latter a comma-separated list), and uploads a
// .csv file (upload only — no paste). "Preview" runs a no-write dry-run and renders the per-SKU
// Added/Removed/Unchanged/Rejected diff; "Apply" (enabled only after a dry-run) performs the
// writes. Matches the Salty orange/beige visual language with framer-motion gated by reduced-motion.
const BulkPackagingImportDialog: React.FC<BulkPackagingImportDialogProps> = ({ open, onClose, onApplied }) => {
    const reduceMotion = useReducedMotion();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [csv, setCsv] = useState('');
    const [fileName, setFileName] = useState<string | null>(null);
    const [results, setResults] = useState<PackagingPerSkuDiff[] | null>(null);
    const [applied, setApplied] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const resetState = () => {
        setCsv('');
        setFileName(null);
        setResults(null);
        setApplied(false);
        setError(null);
    };

    const handleClose = () => {
        if (previewMutation.isPending || applyMutation.isPending) return;
        resetState();
        onClose();
    };

    const handleFile = (file: File | undefined) => {
        if (!file) return;
        // Accept only .csv — guard here in addition to the input's accept filter.
        const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
        if (!isCsv) {
            setError('Please upload a .csv file. Download the template for the correct format.');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setCsv(String(reader.result || ''));
            setFileName(file.name);
            setResults(null);
            setApplied(false);
            setError(null);
        };
        reader.onerror = () => setError('Could not read that file. Please try again.');
        reader.readAsText(file);
    };

    const handleDownloadTemplate = () => {
        const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'packaging-mapping-template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const previewMutation = useMutation({
        mutationFn: () => WarehouseAPI.bulkImportPackagingMappings({ csv, dryRun: true }),
        onSuccess: (res) => {
            setResults(res.results);
            setApplied(false);
            setError(null);
        },
        onError: (err: any) => setError(err?.response?.data?.message || err?.message || 'Failed to preview import'),
    });

    const applyMutation = useMutation({
        mutationFn: () => WarehouseAPI.bulkImportPackagingMappings({ csv, dryRun: false }),
        onSuccess: (res) => {
            setResults(res.results);
            setApplied(true);
            setError(null);
            onApplied();
        },
        onError: (err: any) => setError(err?.response?.data?.message || err?.message || 'Failed to apply import'),
    });

    const totals = results
        ? results.reduce(
              (acc, r) => ({
                  added: acc.added + r.added.length,
                  removed: acc.removed + r.removed.length,
                  unchanged: acc.unchanged + r.unchanged.length,
                  rejected: acc.rejected + r.rejected.length,
              }),
              { added: 0, removed: 0, unchanged: 0, rejected: 0 }
          )
        : null;

    // Per-axis rejected-row counts for the summary line. A row is counted as main-SKU rejected
    // iff mainSkuValid===false; as packaging rejected iff it's still a valid main but had some
    // packaging rejected.
    const rejectedCounts = results
        ? results.reduce(
              (acc, r) => ({
                  mainRejected: acc.mainRejected + (r.mainSkuValid === false ? 1 : 0),
                  packagingRejected:
                      acc.packagingRejected + (r.mainSkuValid !== false && r.rejected.length > 0 ? 1 : 0),
              }),
              { mainRejected: 0, packagingRejected: 0 }
          )
        : null;

    const hasInvalidRows = !!results && results.some(isInvalidRow);

    const handleDownloadInvalidRows = () => {
        if (!results) return;
        const csvContent = buildInvalidRowsCsv(results);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const istDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
        const a = document.createElement('a');
        a.href = url;
        a.download = `invalid-rows-${istDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const motionProps = reduceMotion
        ? { initial: false as const }
        : {
              initial: { opacity: 0, y: 14, scale: 0.97 },
              animate: { opacity: 1, y: 0, scale: 1 },
              transition: { type: 'spring' as const, stiffness: 340, damping: 28 },
          };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            slotProps={{ transition: { timeout: 260 } }}
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 24px 60px -12px rgba(220, 88, 41,0.25), 0 8px 24px -8px rgba(26, 20, 15,0.15)',
                },
            }}
        >
            <motion.div {...motionProps}>
                {/* Header */}
                <Box sx={{
                    px: 3, pt: 2.5, pb: 2,
                    background: 'linear-gradient(135deg, rgba(220, 88, 41, 0.06) 0%, #ffffff 65%)',
                    borderBottom: '1px solid rgba(220, 88, 41, 0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: '12px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: `linear-gradient(135deg, #E37E5A 0%, ${tokens.orangeDark} 100%)`,
                            boxShadow: '0 4px 14px rgba(220, 88, 41,0.35)',
                        }}>
                            <PlaylistAddCheck sx={{ fontSize: 20, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.25 }}>
                                Import packaging mappings
                            </Typography>
                            <Typography variant="caption" sx={{ color: tokens.muted, fontWeight: 500 }}>
                                Preview the diff, then apply — nothing is written until you apply
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        disabled={previewMutation.isPending || applyMutation.isPending}
                        sx={{ color: '#000000', '&:hover': { bgcolor: 'rgba(220, 88, 41, 0.08)', color: tokens.orangeDark } }}
                    >
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="overline" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#a3a3c2', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
                        Upload a .csv — 2 columns: SKU and Packaging SKUs (comma-separated)
                    </Typography>

                    {/* Template download + file dropzone */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        style={{ display: 'none' }}
                        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
                    />
                    <Box
                        onClick={() => fileInputRef.current?.click()}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                        sx={{
                            mb: 1.5, px: 3, py: 3, borderRadius: '12px', cursor: 'pointer',
                            border: `1.5px dashed ${fileName ? tokens.orange : '#d8d2c6'}`,
                            bgcolor: fileName ? 'rgba(220, 88, 41, 0.04)' : '#FAFAF7',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                            textAlign: 'center', transition: 'border-color 150ms, background-color 150ms',
                            '&:hover': { borderColor: tokens.orange, bgcolor: 'rgba(220, 88, 41, 0.04)' },
                        }}
                    >
                        {fileName ? (
                            <>
                                <InsertDriveFile sx={{ fontSize: 26, color: tokens.orange }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', fontFamily: '"JetBrains Mono", monospace' }}>{fileName}</Typography>
                                <Typography variant="caption" sx={{ color: tokens.muted }}>Click to choose a different .csv</Typography>
                            </>
                        ) : (
                            <>
                                <UploadFile sx={{ fontSize: 26, color: tokens.muted }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Click to upload a .csv file</Typography>
                                <Typography variant="caption" sx={{ color: tokens.muted }}>Only .csv files are accepted</Typography>
                            </>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5, flexWrap: 'wrap' }}>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<Download sx={{ fontSize: 16 }} />}
                            onClick={handleDownloadTemplate}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', color: tokens.orangeDark }}
                        >
                            Download template
                        </Button>
                        {hasInvalidRows && (
                            <motion.div whileTap={reduceMotion ? undefined : { scale: 0.96 }} style={{ display: 'inline-block' }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<Download sx={{ fontSize: 16 }} />}
                                    onClick={handleDownloadInvalidRows}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', color: '#b91c1c' }}
                                >
                                    Download invalid rows
                                </Button>
                            </motion.div>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <motion.div whileTap={reduceMotion ? undefined : { scale: 0.96 }} style={{ display: 'inline-block' }}>
                            <Button
                                variant="outlined"
                                onClick={() => previewMutation.mutate()}
                                disabled={previewMutation.isPending || !csv.trim()}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5 }}
                            >
                                {previewMutation.isPending ? <CircularProgress size={18} /> : 'Preview'}
                            </Button>
                        </motion.div>
                        <motion.div whileTap={reduceMotion ? undefined : { scale: 0.96 }} style={{ display: 'inline-block' }}>
                            <Button
                                variant="contained"
                                onClick={() => applyMutation.mutate()}
                                disabled={applyMutation.isPending || !results || applied}
                                sx={{
                                    textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, py: 1,
                                    background: `linear-gradient(135deg, #E37E5A 0%, ${tokens.orangeDark} 100%)`,
                                    boxShadow: '0 4px 14px rgba(220, 88, 41,0.35)',
                                    '&:hover': { boxShadow: '0 6px 18px rgba(220, 88, 41,0.45)' },
                                    '&.Mui-disabled': { background: '#e5e7eb', boxShadow: 'none' },
                                }}
                            >
                                {applyMutation.isPending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Apply'}
                            </Button>
                        </motion.div>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{error}</Alert>
                    )}

                    {results && totals && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                <Chip
                                    label={applied ? 'Applied' : 'Dry-run preview'}
                                    size="small"
                                    sx={{ fontWeight: 700, bgcolor: applied ? '#dcfce7' : '#fef3c7', color: applied ? '#15803d' : '#92400e' }}
                                />
                                <Typography variant="caption" sx={{ color: tokens.muted, fontWeight: 600 }}>
                                    {results.length} SKU{results.length === 1 ? '' : 's'} · {totals.added} added · {totals.removed} removed · {totals.unchanged} unchanged · {totals.rejected} rejected
                                    {rejectedCounts && (rejectedCounts.mainRejected > 0 || rejectedCounts.packagingRejected > 0)
                                        ? ` · ${rejectedCounts.mainRejected} main-SKU rejected · ${rejectedCounts.packagingRejected} packaging rejected`
                                        : ''}
                                </Typography>
                            </Box>

                            {results.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: '10px' }}>No SKU rows were parsed from the CSV.</Alert>
                            ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', maxHeight: 360 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#FAFAF7', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.05, color: '#4A433D' } }}>
                                                <TableCell>Status</TableCell>
                                                <TableCell>SKU</TableCell>
                                                <TableCell>Added</TableCell>
                                                <TableCell>Removed</TableCell>
                                                <TableCell>Unchanged</TableCell>
                                                <TableCell>Rejected</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {results.map((r) => {
                                                const mainRejected = r.mainSkuValid === false;
                                                const packagingRejected = !mainRejected && r.rejected.length > 0;
                                                const dash = <Typography variant="caption" color="text.disabled">—</Typography>;
                                                return (
                                                <TableRow key={r.sku} hover>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        {mainRejected ? (
                                                            <Chip label="Main SKU invalid" size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#fee2e2', color: '#b91c1c' }} />
                                                        ) : packagingRejected ? (
                                                            <Chip label="Packaging rejected" size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#fef3c7', color: '#92400e' }} />
                                                        ) : (
                                                            <Chip label="Imported" size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#dcfce7', color: '#15803d' }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, fontSize: '0.75rem', verticalAlign: 'top', color: mainRejected ? '#b91c1c' : undefined }}>
                                                        {r.sku}
                                                        {mainRejected && (
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#b91c1c', fontFamily: 'inherit', fontWeight: 500 }}>
                                                                {MAIN_SKU_REASON}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        {mainRejected ? dash : (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {r.added.length === 0 ? dash
                                                                    : r.added.map(s => <Chip key={s} label={s} size="small" sx={{ ...chipMono, bgcolor: '#dcfce7', color: '#15803d' }} />)}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        {mainRejected ? dash : (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {r.removed.length === 0 ? dash
                                                                    : r.removed.map(s => <Chip key={s} label={s} size="small" sx={{ ...chipMono, bgcolor: '#fee2e2', color: '#b91c1c' }} />)}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        {mainRejected ? dash : (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {r.unchanged.length === 0 ? dash
                                                                    : r.unchanged.map(s => <Chip key={s} label={s} size="small" sx={{ ...chipMono, bgcolor: '#f1f1f4', color: tokens.muted }} />)}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {r.rejected.length === 0 ? dash
                                                                : r.rejected.map(rej => (
                                                                    <Chip
                                                                        key={rej.sku}
                                                                        label={rej.sku}
                                                                        size="small"
                                                                        sx={{ ...chipMono, bgcolor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
                                                                    />
                                                                ))}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </DialogContent>
            </motion.div>
        </Dialog>
    );
};

export default BulkPackagingImportDialog;
